import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import ProfileSettings from '@Services/ProfileSettings';
import { ProfileSettingsConfig } from '@Constants/rx-user/constants';
import { setImmutable } from '@Pages/ProfileSettings/Preferences/utils/setImmutable';
import { SingleSelect } from '@els/els-react--select';
import { Checkbox } from '@els/els-react--checkbox';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import { SlideSwitch } from '@els/els-react--slide-switch';
import RPSSwitchIcon from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/RPSSwitchIcon';
import {
  isRCSAvailable as isRCSAvailableFn,
  isRPSAvailable as isRPSAvailableFn,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/mapRCSToRPS';
import { RPS_LIBRARIES } from '@Pages/RetroSynthesis/ProjectParametersDialog/constants';
import PreferencesTooltip from '@Pages/ProfileSettings/Preferences/components/PreferencesTooltip';
import ProfileSettingsAccordionItem from '@Pages/ProfileSettings/Components/ProfileSettingsAccordionItem';
import { PREDICTED_PROCESSING_TIME_INTERVALS } from '@Pages/RetroSynthesis/constants';
import { getPredictedProcessingTimeIntervalUpdatedValue, getPredictedProcessingTimeLabelByValue } from '@Pages/RetroSynthesis/utils';

import './SynthesisPlannerPredicted.scss';

const buildPredictionPlanOptions = (editUser) => {
  const PREDICTION_PLAN_LIBRARIES_KEY = 'PREDICTION_PLAN.RX2_PREDICTION_PLAN_LIBRARIES';
  const combinedInfo = ProfileSettings.getCombinedInfo();
  const optionsObject = ProfileSettings.getModelValue(combinedInfo, PREDICTION_PLAN_LIBRARIES_KEY).preferences;
  const keys = optionsObject ? Object.keys(optionsObject) : [];
  const predictionPlanOptions = keys.map((option) => {
    const fullKey = `${PREDICTION_PLAN_LIBRARIES_KEY}.${option}`;
    const values = get(optionsObject, [option, 'values']);
    const onlyOneValue = values && values.length === 1 ? get(values, [0, 'value']) : null;
    const explicitlyDefinedValue = ProfileSettings.getModelValue(editUser, fullKey);
    const defaultValue = values
      ? get(
          values.find((value) => value && value.defaultValue),
          ['value']
        )
      : null;

    return {
      label: optionsObject[option].label,
      name: optionsObject[option].name,
      property: `RX2_PREDICTION_PLAN_LIBRARIES.${option}`,
      id: `prediction_plan_option_${option}`,
      disabled: !isNil(onlyOneValue),
      /* If option has only one value - use this value,
       * if more than one - check if it explicitly defined in /extended response::
       *   if yes - use value from /extended response
       *   if no - use defaultValue from /cobinedinfo
       */
      value: (() => {
        if (!isNil(onlyOneValue)) {
          return onlyOneValue;
        }
        if (!isNil(explicitlyDefinedValue)) {
          return explicitlyDefinedValue;
        }
        return defaultValue;
      })(),
    };
  });

  return predictionPlanOptions;
};

const buildPredictionPlanOptionsWithoutRPS = (editUser) => {
  let predictionPlanOptions = buildPredictionPlanOptions(editUser);
  // Exclude RPS libraries
  predictionPlanOptions = predictionPlanOptions.filter((option) => !RPS_LIBRARIES.includes(option.name));

  return predictionPlanOptions;
};

const buildPredictionPlanOptionsOnlyRPS = (editUser) => {
  let predictionPlanOptions = buildPredictionPlanOptions(editUser);
  // Include only RPS libraries
  predictionPlanOptions = predictionPlanOptions.filter((option) => RPS_LIBRARIES.includes(option.name));

  return predictionPlanOptions;
};

function SynthesisPlannerPredicted({ preferences, options, editUser, onChange, onSave, onReset, onClose }) {
  const [predictionPlanOptions, setPredictionPlanOptions] = useState(() => buildPredictionPlanOptionsWithoutRPS(editUser));

  const isRCSAvailable = useMemo(() => isRCSAvailableFn(options), [options]);
  const isRPSAvailable = useMemo(() => isRPSAvailableFn(options), [options]);

  useMemo(() => {
    setPredictionPlanOptions(buildPredictionPlanOptionsWithoutRPS(editUser));
  }, [editUser]);

  const enabledLibraries = useMemo(
    () => (predictionPlanOptions ? predictionPlanOptions.filter((lib) => !!lib.value) : []),
    [predictionPlanOptions]
  );
  const hasAnyLibraries = enabledLibraries.length > 0;

  const fullStepsValues =
    options.RX2_PREDICTION_PLAN_SHOW_FULL_STEPS && options.RX2_PREDICTION_PLAN_SHOW_FULL_STEPS.values
      ? options.RX2_PREDICTION_PLAN_SHOW_FULL_STEPS.values.slice().reverse()
      : [];

  const renderValue = () => [
    {
      title: ProfileSettingsConfig.Labels.numberOfPlansToCreate,
      value: preferences.RX2_PREDICTION_PLAN_SHOW_FULL_STEPS
        ? `${ProfileSettingsConfig.Labels.numberOfPlansToCreate} = ${preferences.RX2_PREDICTION_PLAN_DEPTH}`
        : `${ProfileSettingsConfig.Labels.lastStepOnly}`,
    },
    {
      title: ProfileSettingsConfig.Labels.stepsWithIdenticalReaction,
      value: `${ProfileSettingsConfig.Labels.stepsWithIdenticalReaction} = ${preferences.RX2_PREDICTION_PLAN_STEPS}`,
    },
    {
      title: ProfileSettingsConfig.Labels.identicalBuildingBlocks,
      value: `${ProfileSettingsConfig.Labels.identicalBuildingBlocks} = ${preferences.RX2_PREDICTION_PLAN_BLOCKS}`,
    },
    {
      title: ProfileSettingsConfig.Labels.processingTime,
      value: `${ProfileSettingsConfig.Labels.processingTime} = ${getPredictedProcessingTimeLabelByValue(
        preferences.RX2_PREDICTION_PLAN_PROCESSING_TIME
      )}`,
    },
    {
      title: ProfileSettingsConfig.Labels.usedBuildingBlocks,
      value: hasAnyLibraries && `${ProfileSettingsConfig.Labels.usedBuildingBlocks} = ${enabledLibraries.map(({ name }) => name)}`,
    },
  ];

  // update processing time because it may be custom and doesn't match intervals
  const processingTimeValue = getPredictedProcessingTimeIntervalUpdatedValue(Number(preferences.RX2_PREDICTION_PLAN_PROCESSING_TIME));

  const handlePlanOptionChange = (optionIdx, value) => {
    const option = predictionPlanOptions[optionIdx];
    onChange(option.property, value);
    setPredictionPlanOptions((prev) => setImmutable(prev, `${optionIdx}.value`, value));
  };

  useEffect(() => {
    // Set RPS toggle switch
    const predictionPlanRPSOptions = buildPredictionPlanOptionsOnlyRPS(editUser);
    const isRPSChecked = predictionPlanRPSOptions.some((option) => option.value);
    onChange('isRPSChecked', isRPSChecked);
  }, []);

  const onRPSSwitchChange = (event) => {
    const checked = event?.currentTarget?.checked;
    onChange('isRPSChecked', checked);
  };

  return (
    <ProfileSettingsAccordionItem
      onClose={onClose}
      onSave={onSave}
      label={ProfileSettingsConfig.Labels.predictionDefaultSettings}
      value={renderValue()}
      resetButton
      titled
      onReset={onReset}
      validateOnSave
      className="synthesis-planner-predicted-accordion-item preferences-accordion-item">
      <div className="preferences-accordion-item__wrapper">
        <PreferencesTooltip
          name="full-routes"
          label="Length & depth of synthesis plans"
          content={[
            <span key="full-route-explanation">
              The option &quot;Full route&quot; will create routes from the target molecule to available building blocks. You have the
              ability to select the maximum No of routes to be generated between 20 to 100 by using the drop down. The default is 20. Please
              note: building block libraries (see below) have a large influence on the route prediction process. Depending on the selected
              library the No of plans generated and their lengths (No of steps per plan) may vary significantly.
            </span>,
            <span key="last-step-only-explanation">
              The option &quot;Last step only&quot; will create synthesis suggestions for a given target for idea generation, but only show
              the last step of a full route, without taking building block availability into account. The list of last step reaction can
              become very high (several hundred suggestion). When scanning through the suggestions you may select the ones you want to
              investigate further by clicking the corresponding starting material and select &quot;Synthesize&quot;.
            </span>,
          ]}
        />
        <div className="synthesis-planner-predicted-accordion-item__routes-options">
          <fieldset className="synthesis-planner-predicted__fieldset">
            <legend className="sr-only">Length &amp; depth of synthesis plans</legend>
            <ul className="rx-radio_button-group preferences-accordion-item__selectOption_label">
              {fullStepsValues.map((option) => (
                <li key={`full-steps-${option.value}`}>
                  <RadioButtonGroup.Option
                    id={`full-steps-${option.value}`}
                    name="full-steps"
                    label={option.label}
                    value={option.value}
                    checked={preferences.RX2_PREDICTION_PLAN_SHOW_FULL_STEPS === option.value}
                    onChange={() => onChange('RX2_PREDICTION_PLAN_SHOW_FULL_STEPS', option.value)}
                  />
                </li>
              ))}
            </ul>
          </fieldset>

          {preferences.RX2_PREDICTION_PLAN_SHOW_FULL_STEPS && (
            <SingleSelect
              className="preferences-accordion-item__selectOption"
              label={ProfileSettingsConfig.Labels.numberOfPlansToCreate}
              size="small"
              selectedOptions={[preferences.RX2_PREDICTION_PLAN_DEPTH]}
              options={options.RX2_PREDICTION_PLAN_DEPTH.values}
              data-e2e="synthesis-planner-predicted-number-of-plans-to-create-select"
              title={ProfileSettingsConfig.Labels.numberOfPlansToCreate}
              onSelect={(val) => onChange('RX2_PREDICTION_PLAN_DEPTH', val)}
              optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
              required
              hideRequired
              shouldHideLabel
            />
          )}
        </div>
        <hr className="preferences-accordion-item__separator" />
        <PreferencesTooltip
          name="diversity-routes"
          content={[
            <span key="diversity-routes-explanation">
              The tool will consider the settings &quot;occurrence of same reaction step&quot; and &quot;occurrence of same Building
              Block&quot; to filter all predicted plans for a given target molecules accordingly. This will result in a shorter, but more
              diverse list of routes.
            </span>,
          ]}
          label="Diversity of routes"
        />
        <div className="synthesis-planner-predicted-accordion-item__options-wrapper">
          <p className="u-els-font-size-caption">{ProfileSettingsConfig.Labels.allowIdenticalReactionSteps}</p>
          <SingleSelect
            className="preferences-accordion-item-diversity-routes__selectOption"
            label={ProfileSettingsConfig.Labels.allowIdenticalReactionSteps}
            size="small"
            isLabelInline
            selectedOptions={[preferences.RX2_PREDICTION_PLAN_STEPS]}
            data-e2e="synthesis-planner-predicted-allow-identical-reaction-steps-select"
            options={options.RX2_PREDICTION_PLAN_STEPS?.values}
            onSelect={(val) => onChange('RX2_PREDICTION_PLAN_STEPS', val)}
            optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
            required
            hideRequired
            shouldHideLabel
          />
        </div>
        <div className="synthesis-planner-predicted-accordion-item__options-wrapper">
          <p className="u-els-font-size-caption">{ProfileSettingsConfig.Labels.allowIdenticalBuildingBlocks}</p>
          <SingleSelect
            className="preferences-accordion-item-diversity-routes__selectOption"
            label={ProfileSettingsConfig.Labels.allowIdenticalBuildingBlocks}
            size="small"
            isLabelInline
            selectedOptions={[preferences.RX2_PREDICTION_PLAN_BLOCKS]}
            options={options.RX2_PREDICTION_PLAN_BLOCKS?.values}
            data-e2e="synthesis-planner-predicted-allow-identical-building-blocks-select"
            onSelect={(val) => onChange('RX2_PREDICTION_PLAN_BLOCKS', val)}
            optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
            required
            hideRequired
            shouldHideLabel
          />
        </div>
        <hr className="preferences-accordion-item__separator" />

        <div className="preferences-accordion-item__option synthesis-planner-predicted-accordion-item__processing-time">
          <PreferencesTooltip
            name="processing-time"
            content={[
              <span key="processing-time-explanation">
                As the retrosynthesis process can work virtually indefinitely, a processing time limit has to be defined. By the Standard
                option, retrosynthesis computes predictive results for approximately 15 minutes. In case no results have been found with the
                standard option you can set the processing time to Extended, which will run for approximately 30 minutes.
              </span>,
            ]}
            label={ProfileSettingsConfig.Labels.processingTime}
          />
          <fieldset className="synthesis-planner-predicted__fieldset">
            <legend className="sr-only">{ProfileSettingsConfig.Labels.processingTime}</legend>
            <ul className="rx-radio_button-group preferences-accordion-item__selectOption_label">
              {PREDICTED_PROCESSING_TIME_INTERVALS.map((interval) => (
                <li key={interval.value}>
                  <RadioButtonGroup.Option
                    id={`processing-time-${interval.value}`}
                    name="processing-time"
                    label={interval.label}
                    value={interval.value}
                    checked={interval.value === processingTimeValue}
                    onChange={() => onChange('RX2_PREDICTION_PLAN_PROCESSING_TIME', interval.value)}
                  />
                </li>
              ))}
            </ul>
          </fieldset>
        </div>
        <hr className="preferences-accordion-item__separator" />

        <div className="preferences-accordion-item__option synthesis-planner-predicted-accordion-item__buildingLibraries">
          <div className="preferences-accordion-item__option_label">Select Building Block Libraries</div>

          <div className="preferences-accordion-item_content synthesis-planner-predicted-accordion-item__buildingLibraries_list">
            {predictionPlanOptions.map((option, idx) => (
              <div className="accordion-item-checkbox" key={`plan-option-${option.id}`}>
                <Checkbox
                  id={option.id}
                  label={`${option.label} ${option.name ? `(${option.name})` : ''}`}
                  title={option.label}
                  data-e2e={`synthesis-planner-predicted-building-block-library-${option.name}`}
                  checked={!!option.value || false}
                  disabled={!!option.disabled}
                  onChange={(event) => handlePlanOptionChange(idx, event?.currentTarget?.checked)}
                />
              </div>
            ))}
          </div>
        </div>

        {isRCSAvailable && isRPSAvailable && (
          <>
            <hr className="preferences-accordion-item__separator" />
            <div className="synthesis-planner-predicted-accordion-item__published-routes-integration">
              <PreferencesTooltip
                name="published-routes-integration"
                content={[
                  <div key="published-routes-integration">
                    <div className="u-els-font-size-caption u-els-margin-bottom">Published Routes Integration</div>
                    <div>
                      Merges literature routes with predicted routes, adding literature-based steps for non‑commercial intermediates and
                      linking them to the predicted pathway. It also creates combined routes by integrating established literature reactions
                      with computer‑generated steps, starting from your configured starting-material settings. Published routes are limited
                      to five steps and use the cheapest available building blocks.
                    </div>
                  </div>,
                ]}
                label="Published Routes Integration"
              />
              <SlideSwitch
                className="u-els-margin-top"
                disabled={false}
                data-e2e="synthesis-planner-predicted-published-routes-integration-switch"
                checked={preferences.isRPSChecked}
                onChange={onRPSSwitchChange}
                label={
                  <>
                    <RPSSwitchIcon />
                    <span className="u-els-display-inline-block">Combine Predicted and Published routes</span>
                  </>
                }
                switchRight
              />
            </div>
          </>
        )}
      </div>
    </ProfileSettingsAccordionItem>
  );
}

SynthesisPlannerPredicted.propTypes = {
  preferences: PropTypes.object,
  editUser: PropTypes.object,
  options: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
};

export default SynthesisPlannerPredicted;
